import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Dropdown, Menu, Popover, message } from 'antd';
import {
	downloadEquipmentTypesCSVForSupplier,
	equipmentTypesRestCrudThunksForSupplier,
	fetchAllEquipmentForSupplier,
	getEquipmentTypeUploadBulkStatus,
	uploadPartialEquipmentTypesBulk,
} from '../../thunks/equipment_types_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { EquipmentRowDisplay } from '../equipment_row_display/EquipmentRowDisplay';
import { EQUIPMENT_TYPES_CRUD_ACTION_CREATORS } from '../../actions/equipment_types_actions';
import { assetTypesRestCrudThunksForSupplier } from '../../thunks/asset_types_thunks';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	EQUIPMENT_MASTER_FLATFILE_HEADER,
	getFlatfileRecordForEquipment,
} from '../stock_locations_detail_details_page/stock_location_constants';
import moment from 'moment';
import { DATE_FORMAT } from '../../utils/DataFormatterUtils';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS, FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getIsActiveFilterField } from '../../utils/FilterUtils';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import OWPoll from '../common/OWPoll';
import PartialEquipmentTypeUpload from './PartialEquipmentTypeUpload';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';

require('./EquipmentsIndexPage.less');

const EQUIPMENTS_TC = 'equipmentsIndex';

const getMissngEquipmentTypeNumbers = (existing, newList) =>
	existing
		.filter(
			(et) =>
				!newList.find(
					(record) =>
						`${et.id}` === `${record.id}` ||
						nullSafeGetOrElse('equipmentTypeNumber', et, -1) === record.equipmentTypeNumber
				)
		)
		.map((_) => nullSafeGetOrElse('equipmentTypeNumber', _, nullSafeGet('id', _)));

const EquipmentsTemplateIndexPage: FC<any> = ({
	location,
	history,
	updateEquipmentsFilters,
	clearAndUpdateFilters,
	equipmentTypes,
	assetTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	fetchAllEquipment,
	createBulk,
	fetchEquipmentTypes,
	currentUser,
	exportCSVToEmail,
	fetchVendors,
	uploadPartialBulk,
	getBulkUploadStatus,
	vendors,
	fetchMultipleVendors,
	companyConfig,
}): React.ReactElement => {
	const [downloading, setDownloading] = useState(false);
	const [exportingCsv, setExportingCsv] = useState(false);
	const [equipmentTypesForUpload, setEquipmentTypesForUpload] = useState<any[]>([]);

	const [uploadPopupVisible, setUploadPopupVisible] = useState(false);
	const [allVendors, setAllVendors] = useState<any[]>([]);
	const [bulkUploadInProgress, setBulkUploadInProgress] = useState(false);

	const [partialUploadPopupVisible, setPartialUploadPopupVisible] = useState(false);
	const [bulkUploadInitiated, setBulkUploadInitiated] = useState(false);

	const _hideUploadPopup = useCallback(() => setUploadPopupVisible(false), []);
	const _hidePartialUploadPopup = () => setPartialUploadPopupVisible(false);

	const userCurrency = useMemo(() => getCurrency({ currentUser }).id, [currentUser]);

	const onRow = useCallback(
		(record) => ({
			onClick: () =>
				history.push(
					getLinkWIthBackLinkParams(
						location,
						'Back to Equipment Type',
						`/supplier/equipments/detail/${record.id}`
					)
				),
		}),
		[history, location]
	);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${EQUIPMENTS_TC}.${propName}`, equipmentTypes, {}),
		[equipmentTypes]
	);

	const refreshEquipments = useCallback(() => {
		fetchEquipmentTypes(
			{},
			EQUIPMENTS_TC,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchEquipmentTypes, getStateSliceProp]);

	const vendorOptions = useMemo(
		() =>
			allVendors.map((_) => ({
				label: `${_.name}${_.vendorId ? ` - ${_.vendorId}` : ''}`,
				value: _.id,
				alternativeNames: [_.name, ...(_.vendorId ? [_.vendorId] : [])],
			})),
		[allVendors]
	);

	const fetchAndSetAllVendors = useCallback(() => {
		return new Promise((resolve, reject) => {
			fetchVendors()
				.then((res) => {
					setAllVendors(res);
					resolve(true);
				})
				.catch(reject);
		});
	}, [fetchVendors]);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'modelName',
				sorter: true,
				render: (_, record) => <EquipmentRowDisplay equipmentType={record} />,
			},
		],
		[]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Asset Type',
				fieldName: FILTER_CONFIG_NAMES.ASSET_TYPE,
				mode: 'multiple',
				stateSlice: assetTypes,
				targetCollectionName: 'assetTypesIndex',
				fetch: fetchAssetTypes,
				fetchMultiple: fetchMultipleAssetTypes,
			},
			{
				label: 'Default Vendor',
				fieldName: 'defaultVendorIds',
				mode: 'multiple',
				stateSlice: vendors,
				targetCollectionName: 'eqTypesIndex',
				fetch: fetchVendors,
				fetchMultiple: fetchMultipleVendors,
			},
			...[getIsActiveFilterField(updateEquipmentsFilters, EQUIPMENTS_TC, true)],
		],
		[
			assetTypes,
			fetchAssetTypes,
			fetchMultipleAssetTypes,
			updateEquipmentsFilters,
			vendors,
			fetchVendors,
			fetchMultipleVendors,
		]
	);

	const onUploadClick = useCallback(() => {
		setDownloading(true);
		Promise.all([fetchAllEquipment(), fetchAndSetAllVendors()])
			.then((res) => {
				setEquipmentTypesForUpload(res[0]);
				setUploadPopupVisible(true);
			})
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [fetchAllEquipment, fetchAndSetAllVendors]);

	const onPartialUploadClick = useCallback(() => {
		setDownloading(true);
		fetchAndSetAllVendors()
			.then(() => setPartialUploadPopupVisible(true))
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [fetchAndSetAllVendors]);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	const headerDefs = useMemo(
		() => EQUIPMENT_MASTER_FLATFILE_HEADER([userCurrency], [], glCodeEnabled),
		[userCurrency, glCodeEnabled]
	);

	const exportAsCsv = useCallback(
		(ets) => {
			const date = moment().format(DATE_FORMAT);
			const csvTitle = `Equipment_Master_List_${date}`;
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: headerDefs.map((h) => h.label),
			});

			const data =
				ets.length > 0
					? ets.map((_) => getFlatfileRecordForEquipment(_, userCurrency, headerDefs))
					: [getFlatfileRecordForEquipment({}, userCurrency, headerDefs)];
			csvExporter.generateCsv(data);
		},
		[glCodeEnabled, userCurrency]
	);

	const onDownloadSample = useCallback(() => {
		exportAsCsv(equipmentTypesForUpload);
	}, [equipmentTypesForUpload, exportAsCsv]);

	const downloadCSV = useCallback(() => {
		setExportingCsv(true);
		exportCSVToEmail()
			.catch((err) => message.error(err))
			.finally(() => setExportingCsv(false));
	}, [exportCSVToEmail]);

	const supplierFacilityId = useMemo(() => nullSafeGet('facility.id', currentUser), [currentUser]);

	const getImagesFromExisitngEquipment = useCallback(
		(id) => {
			const et = equipmentTypesForUpload.find((_) => `${_.id}` === `${id}`);
			return nullSafeGetOrElse('images', et, []);
		},
		[equipmentTypesForUpload]
	);

	const onDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const missingNumbers = getMissngEquipmentTypeNumbers(
					equipmentTypesForUpload,
					results.validData
				);

				if (missingNumbers.length > 0) {
					reject(
						`Some equipment types are missing from existing list. Please include them! Equipment type # ${missingNumbers.join(
							', '
						)} `
					);
				} else {
					setBulkUploadInProgress(true);
					setBulkUploadInitiated(true);
					createBulk(
						results.validData.map((_) => ({
							..._,
							id: _.id ? parseInt(_.id) : undefined,
							cost: _.cost ? parseFloat(_.cost) : undefined,
							supplierFacilityId,
							images: getImagesFromExisitngEquipment(_.id),
						}))
					)
						.then((res) => {
							resolve('Updated successfully!');
							setTimeout(() => {
								_hideUploadPopup();
							}, 0);
						})
						.catch((err) => {
							reject(err || 'Unable to upload! Please contact support!');
							setBulkUploadInitiated(false);
						})
						.finally(() => setBulkUploadInProgress(false));
				}
			});
		},
		[
			_hideUploadPopup,
			createBulk,
			equipmentTypesForUpload,
			getImagesFromExisitngEquipment,
			supplierFacilityId,
		]
	);

	const onPartialDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const entities = results.validData.map((_) => ({
					..._,
					id: _.id ? parseInt(_.id) : undefined,
					cost: _.cost ? parseFloat(_.cost) : undefined,
				}));

				setBulkUploadInProgress(true);
				setBulkUploadInitiated(true);
				uploadPartialBulk(entities)
					.then((res) => {
						resolve('Upload initiated successfully!');
						setTimeout(() => {
							_hidePartialUploadPopup();
						}, 0);
					})
					.catch((err) => {
						reject(err || 'Unable to upload! Please contact support!');
						setBulkUploadInitiated(false);
					})
					.finally(() => setBulkUploadInProgress(false));
			});
		},
		[uploadPartialBulk]
	);

	const uploadMenu = (
		<Menu>
			<Menu.Item onClick={onUploadClick}>All Equipment Types</Menu.Item>
			<Menu.Item onClick={onPartialUploadClick}>Partial Upload</Menu.Item>
		</Menu>
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={EQUIPMENTS_TC}
				updateFilters={updateEquipmentsFilters}
				stateSlice={equipmentTypes}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="equipment_types"
				tableColumns={columns}
				onTableRow={onRow}
				fetchData={equipmentTypesRestCrudThunksForSupplier.read}
				initialSorters={{
					sort_by: 'modelName',
					order: 'ascend',
				}}
				preAppliedFilters={{ isActive: true }}
				rightActions={
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
						<div className="flex flex-row items-center">
							{bulkUploadInProgress ? null : (
								<OWPoll
									key="et-upload"
									id="et-upload"
									fetchStatusPromises={[getBulkUploadStatus]}
									successMessage="Equipment Type Upload Completed!"
									message="Equipment Type Upload in progress!"
									onRefresh={refreshEquipments}
									successMessageMandatory={bulkUploadInitiated}
									renderElementWithProps={(props) => (
										<Dropdown overlay={uploadMenu} {...props}>
											<Button
												size="large"
												className="inline-block-visible-md ml-2"
												icon={<UploadOutlined />}
												loading={downloading}
											>
												<span className="inline-block-visible-xxl">Upload</span>
											</Button>
										</Dropdown>
									)}
								/>
							)}
							<Popover content="Export CSV" trigger="hover">
								<Button
									size="large"
									icon={<DownloadOutlined />}
									className="inline-block-visible-md ml-2"
									onClick={downloadCSV}
									loading={exportingCsv}
								>
									<span className="inline-block-visible-xxl">Export CSV</span>
								</Button>
							</Popover>
							<Button
								className="ml-2"
								type="primary"
								size="large"
								onClick={() => history.push('/supplier/equipments/overview/new_template')}
							>
								New Equipment Type
							</Button>
						</div>
					</AccessPermissionChecker>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/ficus.svg"
								alt="Add your first equipment"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any equipment yet.
								</div>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
									<Button
										type="primary"
										onClick={() => history.push('/supplier/equipments/overview/new_template')}
									>
										Add your first equipment
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				}
			/>
			{uploadPopupVisible && (
				<UploadWithSampleModal
					type="Equipment Type"
					title="Upload Equipment Types"
					downloadText="Do you want to download the existing Equipment Types List?"
					sampleFileText="Download Equipment Types"
					onCancel={_hideUploadPopup}
					onDownloadSample={onDownloadSample}
					fields={EQUIPMENT_MASTER_FLATFILE_HEADER([userCurrency], vendorOptions, glCodeEnabled)}
					onDataLoad={onDataUpload}
					showWarningMessage
				/>
			)}
			{partialUploadPopupVisible && (
				<PartialEquipmentTypeUpload
					onDataUpload={onPartialDataUpload}
					hideUploadPopup={_hidePartialUploadPopup}
					vendorOptions={vendorOptions}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	equipmentTypes: state.equipment_types,
	assetTypes: state.asset_types,
	currentUser: state.session.currentUser,
	vendors: state.vendors,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchEquipmentTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentTypesRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleAssetTypes: (ids, targetCollectionName) =>
		dispatch(assetTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetTypesRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	updateEquipmentsFilters: (filters, targetCollection) =>
		dispatch(EQUIPMENT_TYPES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			EQUIPMENT_TYPES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	fetchAllEquipment: (params) => dispatch(fetchAllEquipmentForSupplier(params)),
	createBulk: (entities) => dispatch(equipmentTypesRestCrudThunksForSupplier.createBulk(entities)),
	exportCSVToEmail: (params, filters) =>
		dispatch(downloadEquipmentTypesCSVForSupplier(params, filters)),
	fetchVendors: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName
			)
		),
	getBulkUploadStatus: () => dispatch(getEquipmentTypeUploadBulkStatus(ROLE_TYPES.SUPPLIER)()),
	uploadPartialBulk: (entities) =>
		dispatch(uploadPartialEquipmentTypesBulk(ROLE_TYPES.SUPPLIER)(entities)),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(EquipmentsTemplateIndexPage)
);
